import { format } from "date-fns";
import { Bell, Plus, Repeat } from "lucide-react";
import {
  useCreate,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import type { PersonalTodo } from "../types";
import {
  buildNextOccurrence,
  getTodoGroup,
  parseDateOnly,
  type TodoGroup,
} from "./todoSchedule";
import { useDateLocale } from "./useDateLocale";

const RESOURCE = "personal_todos";
const GROUPS: TodoGroup[] = ["overdue", "today", "upcoming"];

export interface TodoListProps {
  todos: PersonalTodo[];
  onAdd: () => void;
  onEdit: (todo: PersonalTodo) => void;
}

const useCompleteTodo = () => {
  const notify = useNotify();
  const { dateFnsLocale } = useDateLocale();
  const [update] = useUpdate();
  const [create] = useCreate();
  const [completingIds, setCompletingIds] = useState<Identifier[]>([]);

  const complete = async (todo: PersonalTodo) => {
    setCompletingIds((ids) => [...ids, todo.id]);
    try {
      await update(
        RESOURCE,
        {
          id: todo.id,
          data: { done_at: new Date().toISOString() },
          previousData: todo,
        },
        { returnPromise: true },
      );
      const next = buildNextOccurrence(todo);
      if (next) {
        await create(RESOURCE, { data: next }, { returnPromise: true });
        notify("crm.personal.todos.next_scheduled", {
          messageArgs: {
            date: format(parseDateOnly(next.due_date), "PPP", {
              locale: dateFnsLocale,
            }),
          },
        });
      } else {
        notify("crm.personal.todos.completed");
      }
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    } finally {
      setCompletingIds((ids) => ids.filter((id) => id !== todo.id));
    }
  };

  return { complete, completingIds };
};

export const TodoList = ({ todos, onAdd, onEdit }: TodoListProps) => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const { complete, completingIds } = useCompleteTodo();

  const grouped = GROUPS.map((group) => ({
    group,
    items: todos.filter((todo) => getTodoGroup(todo.due_date) === group),
  })).filter(({ items }) => items.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">
          {translate("crm.personal.todos.title")}
        </h3>
        <Button
          size="icon"
          variant="outline"
          onClick={onAdd}
          aria-label={translate("crm.personal.todos.add")}
          title={translate("crm.personal.todos.add")}
        >
          <Plus />
        </Button>
      </div>

      {grouped.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {translate("crm.personal.todos.empty")}
        </p>
      )}

      {grouped.map(({ group, items }) => (
        <section key={group} className="flex flex-col gap-1">
          <h4
            className={cn(
              "text-xs font-medium uppercase tracking-wide text-muted-foreground",
              group === "overdue" && "text-destructive",
            )}
          >
            {translate(`crm.personal.todos.groups.${group}`)}
          </h4>
          <ul className="flex flex-col">
            {items.map((todo) => {
              const isCompleting = completingIds.includes(todo.id);
              return (
                <li
                  key={todo.id}
                  className="flex items-start gap-3 rounded-md px-1 py-2 hover:bg-accent/50"
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={isCompleting}
                    disabled={isCompleting}
                    onCheckedChange={() => complete(todo)}
                    aria-label={`${translate("crm.personal.todos.mark_done")}: ${todo.title}`}
                  />
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 flex-col text-left"
                    onClick={() => onEdit(todo)}
                  >
                    <span
                      className={cn(
                        "truncate text-sm font-medium",
                        isCompleting && "text-muted-foreground line-through",
                      )}
                    >
                      {todo.title}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      {format(parseDateOnly(todo.due_date), "PP", {
                        locale: dateFnsLocale,
                      })}
                      {todo.recurrence !== "none" && (
                        <Repeat
                          className="size-3"
                          aria-label={translate("crm.personal.todos.repeats")}
                        />
                      )}
                      {todo.remind_at && !todo.reminded_at && (
                        <Bell className="size-3" aria-hidden />
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
